
import type {
  Identifiable,
  ProductDomainService,
  QueryRanking,
  QueryRankingElementRankingDTO,
} from '@empathyco/playboard-core-domain'
import type { PropType, SetupContext } from 'vue'
import type { ToolsMessages } from '../../models/i18n/tools.messages'
import type { QueryRankingDomainService } from '../../services/interfaces/domain/query-ranking-domain.service'
import type { EpbKeywordsItemId } from '../shared/epb-keywords'
import type { BaseQueryRankingModalAcceptEventPayload } from './base-query-ranking-modal.types'
import type { AcceptEventPayload } from './use-query-ranking-modal'
import { defineComponent, ref, watch } from 'vue'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { ConfigurationsUtils } from '../../utils/configurations.utils'
import { EpbKeywords } from '../shared/epb-keywords'
import BaseQueryRankingModal from './base-query-ranking-modal.component.vue'
import { useQueryRankingModal } from './use-query-ranking-modal'

type ProductsQueryRankingModalAcceptEventPayload = Array<QueryRankingElementRankingDTO>

export default defineComponent({
  name: 'ProductsQueryRankingModal',
  components: {
    EpbKeywords,
    BaseQueryRankingModal,
  },
  props: {
    messages: {
      type: Object as PropType<ToolsMessages['configurations']>,
      required: true,
    },
    queryRanking: {
      type: Object as PropType<QueryRanking>,
      required: true,
    },
    queryRankingConfig: {
      type: Object as PropType<QueryRankingElementRankingDTO & Identifiable>,
      required: false,
      default: null,
    },
  },
  emits: {
    close: () => {},
    accept: (payload: ProductsQueryRankingModalAcceptEventPayload) => {},
  },
  setup(props, ctx: SetupContext) {
    const initialFieldValues = {
      productIds: [],
    }

    const productDomainService = CONTAINER.get<ProductDomainService>(TYPES.ProductDomainService)
    const qrDomainService = CONTAINER.get<QueryRankingDomainService>(
      TYPES.QueryRankingDomainService,
    )

    const queryRankingModal = useQueryRankingModal<
      ProductsQueryRankingModalAcceptEventPayload,
      BaseQueryRankingModalAcceptEventPayload
    >({ resetFields, buildAcceptEventPayload }, ctx)

    const productIds = ref([...initialFieldValues.productIds])
    const productIdsRef = ref()
    const isFormValid = ref(validateForm())

    const validateIds = (productIds: string[]): Promise<EpbKeywordsItemId[]> => {
      const validatedProducts = productIds.map(id => {
        const isIdValid = productDomainService.isProductIdValid(id)
        if (!isIdValid) {
          return {
            id,
            valid: false,
            tooltip: props.messages.fields.products.invalidProductID,
          }
        }
        const hasConflict = qrDomainService.areProductRankingFieldsInConflict(props.queryRanking, {
          id: props.queryRankingConfig
            ? ConfigurationsUtils.getRankingElementId(props.queryRankingConfig)
            : undefined,
          fieldName: qrDomainService.getProductIdField(),
          fieldValue: id,
          schedule: queryRankingModal.schedule.value,
        })

        return {
          id,
          valid: !hasConflict,
          tooltip: hasConflict ? props.messages.modals.products.overlappedResult : undefined,
        }
      })
      return Promise.resolve(validatedProducts)
    }

    function validateForm(): boolean {
      return (
        !!props.queryRankingConfig &&
        !qrDomainService.areProductRankingFieldsInConflict(props.queryRanking, {
          ...props.queryRankingConfig,
          schedule: queryRankingModal.schedule.value,
        })
      )
    }

    function resetFields(): void {
      isFormValid.value = false
      productIds.value = props.queryRankingConfig
        ? [props.queryRankingConfig.fieldValue]
        : [...initialFieldValues.productIds]
    }

    function buildAcceptEventPayload(
      acceptEventPayload: AcceptEventPayload,
    ): ProductsQueryRankingModalAcceptEventPayload {
      return productIds.value.map((productId: string) =>
        qrDomainService.createProductRanking({
          fieldValue: productId,
          schedule: acceptEventPayload.schedule,
          action: acceptEventPayload.action,
          filters: {},
        }),
      )
    }

    watch(
      () => queryRankingModal.schedule,
      () => {
        if (props.queryRankingConfig) {
          isFormValid.value = validateForm()
        }

        if (!props.queryRankingConfig && productIds.value.length) {
          productIdsRef.value?.validatePillIds()
        }
      },
      { deep: true },
    )

    return {
      ...queryRankingModal,
      productIdsRef,
      isFormValid,
      productIds,
      validateIds,
    }
  },
})
